import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import api from "./api";

const APP_ID = "com.whitentongroup.tracker";
const NATIVE_PUSH_ENABLED = process.env.REACT_APP_ENABLE_NATIVE_PUSH === "true";
let activeUserId = null;
let registeredUserId = null;
let currentToken = null;
let listenersPromise = null;
let setupPromise = null;
let tokenUpload = null;
let retryTimer = null;
let retryAttempt = 0;
let generation = 0;

function available() {
  return NATIVE_PUSH_ENABLED && Capacitor.isNativePlatform();
}

function clearRetry() {
  if (retryTimer) window.clearTimeout(retryTimer);
  retryTimer = null;
}

function retryLater() {
  if (!activeUserId || retryTimer) return;
  const delay = Math.min(5000 * (2 ** retryAttempt++), 60000);
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    initializePushNotifications({ id: activeUserId });
  }, delay);
}

async function uploadToken() {
  if (!activeUserId || !currentToken) return;
  const userId = activeUserId;
  const currentGeneration = generation;
  const request = api.post("/me/push-devices", {
    token: currentToken, platform: Capacitor.getPlatform(), app_id: APP_ID,
  });
  tokenUpload = request;
  try {
    await request;
    if (currentGeneration === generation && userId === activeUserId) {
      registeredUserId = userId;
      retryAttempt = 0;
      clearRetry();
    }
  } catch {
    if (currentGeneration === generation) retryLater();
  } finally {
    if (tokenUpload === request) tokenUpload = null;
  }
}

async function registerListeners() {
  if (listenersPromise) return listenersPromise;
  listenersPromise = (async () => {
    const handles = [];
    try {
      handles.push(await PushNotifications.addListener("registration", async (token) => {
        if (!activeUserId) return;
        currentToken = token.value;
        await uploadToken();
      }));
      handles.push(await PushNotifications.addListener("registrationError", () => retryLater()));
      handles.push(await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
        const data = event?.notification?.data || {};
        const path = typeof data.path === "string" ? data.path :
          data.project_id ? `/projects/${encodeURIComponent(data.project_id)}` : "/";
        // Notification data must never navigate the authenticated WebView to another origin.
        if (path.startsWith("/") && !path.startsWith("//") && !path.includes("\\")) window.location.assign(path);
      }));
      const retryNow = () => {
        if (!activeUserId || document.visibilityState === "hidden") return;
        registeredUserId = null;
        clearRetry();
        initializePushNotifications({ id: activeUserId });
      };
      window.addEventListener("online", retryNow);
      document.addEventListener("visibilitychange", retryNow);
    } catch (error) {
      await Promise.all(handles.map(handle => handle.remove()));
      listenersPromise = null;
      throw error;
    }
  })();
  return listenersPromise;
}

export async function initializePushNotifications(user) {
  if (!available() || !user?.id) return;
  if (activeUserId !== user.id) {
    activeUserId = user.id;
    registeredUserId = null;
    generation += 1;
  }
  if (registeredUserId === user.id) return;
  if (setupPromise) return setupPromise;
  const currentGeneration = generation;
  setupPromise = (async () => {
    try {
      await registerListeners();
      if (Capacitor.getPlatform() === "android") {
        await PushNotifications.createChannel({
          id: "tracker_updates", name: "Tracker Updates", description: "Whitenton Tracker notifications",
          importance: 4, visibility: 1, sound: "default",
        });
      }
      let permission = await PushNotifications.checkPermissions();
      if (["prompt", "prompt-with-rationale"].includes(permission.receive)) {
        permission = await PushNotifications.requestPermissions();
      }
      if (permission.receive !== "granted" || currentGeneration !== generation) return;
      if (currentToken) await uploadToken();
      else {
        await PushNotifications.register();
        if (currentGeneration === generation && registeredUserId !== activeUserId) retryLater();
      }
    } catch {
      if (currentGeneration === generation) retryLater();
    } finally {
      setupPromise = null;
    }
  })();
  return setupPromise;
}

export async function unregisterPushNotifications() {
  if (!available()) return;
  const token = currentToken;
  activeUserId = null;
  registeredUserId = null;
  generation += 1;
  clearRetry();
  if (setupPromise) await setupPromise;
  if (tokenUpload) await tokenUpload.catch(() => {});
  currentToken = null;
  try {
    if (token) await api.post("/me/push-devices/unregister", { token });
  } finally {
    try { await PushNotifications.unregister(); } catch { /* The next login retries registration. */ }
  }
}
